(() => {
  const formatPrice = (value) => new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

  const formatDate = (value) => {
    const raw = String(value || "");
    const dateOnly = raw.split("T", 1)[0];
    const parts = dateOnly.split("-");
    const day = parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : raw;
    const time = raw.includes("T") ? raw.split("T")[1]?.slice(0, 5) : "";
    return time ? `${day} ${time}` : day;
  };

  const pointTime = (value) => String(value?.t || value?.date || "");

  const normalizePoints = (points) => {
    const byTime = new Map();
    (Array.isArray(points) ? points : []).forEach((point, index) => {
      const t = pointTime(point) || new Date(Date.now() + index).toISOString();
      const price = Number(point?.price || 0);
      if (!t || price <= 0) return;
      byTime.set(`${t}:${price}`, {
        t,
        date: t,
        price,
        compare_at: Number(point?.compare_at || 0),
      });
    });
    return Array.from(byTime.values()).sort((a, b) => pointTime(a).localeCompare(pointTime(b)));
  };

  const selectedVariantId = (root) => {
    const form = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return String(form?.value || root.dataset.variantId || "");
  };

  const variantsFromRoot = (root) => {
    try {
      return JSON.parse(root.querySelector("[data-price-history-variants]")?.textContent || "[]");
    } catch (_error) {
      return [];
    }
  };

  const storageKey = (root, variantId) =>
    `genki:tcg-price-history:${root.dataset.productId || "product"}:${variantId || "variant"}`;

  const readLivePoints = (root, variantId) => {
    try {
      return normalizePoints(JSON.parse(window.localStorage.getItem(storageKey(root, variantId)) || "[]"));
    } catch (_error) {
      return [];
    }
  };

  const writeLivePoints = (root, variantId, points) => {
    try {
      window.localStorage.setItem(storageKey(root, variantId), JSON.stringify(points.slice(-720)));
    } catch (_error) {
      /* localStorage can be blocked; the chart still works for the current page. */
    }
  };

  const mergePoints = (...groups) => normalizePoints(groups.flat());

  const updateRootVariantPrice = (root, variantId, price, title = "") => {
    const variants = variantsFromRoot(root);
    const next = variants.map((variant) => String(variant.id) === String(variantId)
      ? { ...variant, price, title: title || variant.title }
      : variant);
    const script = root.querySelector("[data-price-history-variants]");
    if (script) script.textContent = JSON.stringify(next);
  };

  const svgNode = (name, attributes = {}) => {
    const node = document.createElementNS("http://www.w3.org/2000/svg", name);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };

  const renderChart = (target, points) => {
    target.replaceChildren();
    if (!points.length) {
      const empty = document.createElement("p");
      empty.className = "genki-price-history__empty";
      empty.textContent = "Noch keine Preisänderung dokumentiert.";
      target.appendChild(empty);
      return;
    }

    const width = 860;
    const height = 260;
    const padding = { top: 28, right: 28, bottom: 44, left: 72 };
    const prices = points.map((point) => Number(point.price));
    let min = Math.min(...prices);
    let max = Math.max(...prices);
    if (min === max) {
      const room = Math.max(1, min * .08);
      min -= room;
      max += room;
    }
    const x = (index) => padding.left + (index / Math.max(1, points.length - 1)) * (width - padding.left - padding.right);
    const y = (price) => padding.top + ((max - price) / (max - min)) * (height - padding.top - padding.bottom);

    const svg = svgNode("svg", {
      viewBox: `0 0 ${width} ${height}`,
      role: "img",
      "aria-label": `Preisverlauf von ${formatPrice(Math.min(...prices))} bis ${formatPrice(Math.max(...prices))}`,
    });

    for (let index = 0; index <= 4; index += 1) {
      const gridY = padding.top + (index / 4) * (height - padding.top - padding.bottom);
      const gridPrice = max - (index / 4) * (max - min);
      svg.appendChild(svgNode("line", {
        x1: padding.left,
        y1: gridY,
        x2: width - padding.right,
        y2: gridY,
        class: "genki-price-history__grid",
      }));
      const label = svgNode("text", {
        x: padding.left - 10,
        y: gridY + 4,
        "text-anchor": "end",
        class: "genki-price-history__axis",
      });
      label.textContent = formatPrice(gridPrice);
      svg.appendChild(label);
    }

    const coordinates = points.map((point, index) => [x(index), y(Number(point.price))]);
    const legend = svgNode("text", {
      x: padding.left,
      y: 16,
      class: "genki-price-history__legend",
    });
    legend.textContent = "Live registrierter Preis (EUR)";
    svg.appendChild(legend);

    if (coordinates.length === 1) {
      const [pointX, pointY] = coordinates[0];
      svg.appendChild(svgNode("line", {
        x1: Math.max(padding.left, pointX - 34),
        y1: pointY,
        x2: Math.min(width - padding.right, pointX + 34),
        y2: pointY,
        class: "genki-price-history__line",
      }));
    } else {
      svg.appendChild(svgNode("polyline", {
        points: coordinates.map(([pointX, pointY]) => `${pointX},${pointY}`).join(" "),
        class: "genki-price-history__line",
      }));
    }

    coordinates.forEach(([pointX, pointY], index) => {
      const previousPrice = index > 0 ? Number(points[index - 1].price) : Number(points[index].price);
      const currentPrice = Number(points[index].price);
      const directionClass = currentPrice > previousPrice
        ? " genki-price-history__dot--up"
        : currentPrice < previousPrice
          ? " genki-price-history__dot--down"
          : "";
      const dot = svgNode("circle", {
        cx: pointX,
        cy: pointY,
        r: 4.5,
        class: `genki-price-history__dot${directionClass}`,
      });
      const title = svgNode("title");
      title.textContent = `${formatDate(pointTime(points[index]))}: ${formatPrice(points[index].price)}`;
      dot.appendChild(title);
      svg.appendChild(dot);
    });

    const labelIndexes = [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])];
    labelIndexes.forEach((index) => {
      const label = svgNode("text", {
        x: x(index),
        y: height - 16,
        "text-anchor": index === 0 ? "start" : index === points.length - 1 ? "end" : "middle",
        class: "genki-price-history__axis",
      });
      label.textContent = formatDate(pointTime(points[index]));
      svg.appendChild(label);
    });
    target.appendChild(svg);
  };

  const renderVariant = (root, productEntry, variantId) => {
    const variants = variantsFromRoot(root);
    const currentVariant = variants.find((variant) => String(variant.id) === String(variantId)) || variants[0] || {};
    const storedVariants = productEntry?.variants || {};
    const stored = storedVariants[String(variantId)]
      || storedVariants[String(root.dataset.productId)]
      || Object.values(storedVariants)[0];
    const points = mergePoints(Array.isArray(stored?.points) ? stored.points.slice() : [], readLivePoints(root, variantId));
    const currentPrice = Number(currentVariant.price || points.at(-1)?.price || 0);

    if (currentPrice > 0 && !points.length) {
      const initial = [{ t: new Date().toISOString(), price: currentPrice }];
      writeLivePoints(root, variantId, initial);
      points.push(...initial);
    }

    const prices = points.map((point) => Number(point.price)).filter((price) => price > 0);
    root.querySelector("[data-price-history-current]").textContent = formatPrice(currentPrice);
    root.querySelector("[data-price-history-low]").textContent = prices.length ? formatPrice(Math.min(...prices)) : "--";
    root.querySelector("[data-price-history-high]").textContent = prices.length ? formatPrice(Math.max(...prices)) : "--";
    root.querySelector("[data-price-history-since]").textContent = points.length ? formatDate(pointTime(points[0])) : "--";
    root.querySelector("[data-price-history-variant]").textContent =
      currentVariant.title && currentVariant.title !== "Default Title" ? currentVariant.title : "Produktpreis";
    root.querySelector("[data-price-history-note]").textContent = points.length <= 1
      ? "Die Live-Preisaufzeichnung fuer diesen TCG-Artikel startet jetzt."
      : "Angezeigt werden live registrierte Genkidama-Shoppreise.";
    renderChart(root.querySelector("[data-price-history-chart]"), points);
  };

  const pollCurrentPrice = async (root, productEntry) => {
    const handle = root.dataset.productHandle;
    const variantId = selectedVariantId(root);
    if (!handle || !variantId) return;
    const response = await fetch(`/products/${handle}.js?_=${Date.now()}`, {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const product = await response.json();
    const variant = (product.variants || []).find((item) => String(item.id) === String(variantId));
    const price = Number(variant?.price || 0) / 100;
    if (price <= 0) return;

    updateRootVariantPrice(root, variantId, price, variant?.title || "");
    const points = readLivePoints(root, variantId);
    points.push({ t: new Date().toISOString(), price });
    writeLivePoints(root, variantId, points);
    renderVariant(root, productEntry, variantId);
  };

  const init = async (root) => {
    try {
      const response = await fetch(root.dataset.historyUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      const productEntry = payload.products?.[String(root.dataset.productId)] || null;
      const rerender = () => renderVariant(root, productEntry, selectedVariantId(root));
      rerender();
      pollCurrentPrice(root, productEntry).catch(() => {});
      window.setInterval(() => {
        pollCurrentPrice(root, productEntry).catch(() => {});
      }, 30000);
      document.addEventListener("change", (event) => {
        if (event.target?.matches('form[action*="/cart/add"] [name="id"], variant-selects select, variant-radios input')) {
          window.setTimeout(() => pollCurrentPrice(root, productEntry).catch(rerender), 0);
        }
      });
    } catch (_error) {
      renderVariant(root, null, selectedVariantId(root));
      pollCurrentPrice(root, null).catch(() => {});
      window.setInterval(() => {
        pollCurrentPrice(root, null).catch(() => {});
      }, 30000);
    }
  };

  document.querySelectorAll("[data-genki-price-history]").forEach(init);
})();
